import { listRegularUsers, type UserFilterParams, type UserResponse } from '@api/users-api';
import Breadcrumbs from '@components/breadcrumbs';
import TableFilters from '@components/table-filters';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, RefreshCw, Users } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import UserVotesModal from './user-votes-modal';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDpId(user: UserResponse): string {
  if (user.docplannerClinicId != null) return String(user.docplannerClinicId);
  if (user.docplannerDoctorId != null) return String(user.docplannerDoctorId);
  return '—';
}

function getProfessionalType(user: UserResponse): string {
  if (user.docplannerClinicId != null) return 'Clinic';
  if (user.docplannerDoctorId != null) return 'Doctor';
  return '—';
}

function getPageRange(currentPage: number, totalPages: number): number[] {
  const MAX_VISIBLE = 7;

  if (totalPages <= MAX_VISIBLE) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: number[] = [1];

  let start = Math.max(2, currentPage - 1);
  let end = Math.min(totalPages - 1, currentPage + 1);

  if (currentPage <= 3) {
    start = 2;
    end = 5;
  } else if (currentPage >= totalPages - 2) {
    start = totalPages - 4;
    end = totalPages - 1;
  }

  if (start > 2) pages.push(-1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push(-2);

  pages.push(totalPages);
  return pages;
}

export default function RegularUsers(): React.JSX.Element {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [providerFilter, setProviderFilter] = useState('');
  const [debouncedProvider, setDebouncedProvider] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);
  const providerDebounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  useEffect(() => {
    if (providerDebounceRef.current) clearTimeout(providerDebounceRef.current);
    providerDebounceRef.current = setTimeout(() => {
      setDebouncedProvider(providerFilter);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (providerDebounceRef.current) clearTimeout(providerDebounceRef.current);
    };
  }, [providerFilter]);

  const filters: UserFilterParams = {
    ...(debouncedSearch ? { email: debouncedSearch } : {}),
    ...(debouncedProvider ? { provider: debouncedProvider } : {}),
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['regular-users', currentPage, debouncedSearch, debouncedProvider],
    queryFn: () => listRegularUsers({ page: currentPage, pageSize: PAGE_SIZE }, filters),
    staleTime: 30_000,
  });

  const users = data?.data ?? [];
  const totalItems = data?.meta?.totalItems ?? 0;
  const totalPages = data?.meta?.totalPages ?? 1;
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * PAGE_SIZE, totalItems);

  const goToPage = useCallback((page: number | ((prev: number) => number)): void => {
    setCurrentPage(page);
  }, []);

  const hasActiveFilters = Boolean(debouncedSearch || debouncedProvider);

  const clearFilters = useCallback((): void => {
    setSearch('');
    setDebouncedSearch('');
    setProviderFilter('');
    setDebouncedProvider('');
    setCurrentPage(1);
  }, []);

  return (
    <>
      <Breadcrumbs items={[{ label: 'Editions', to: '/editions' }, { label: 'Regular Users' }]} />

      <div className="page-header">
        <div className="page-header__left">
          <h1 className="page-header__title">Regular Users</h1>
          <p className="page-header__subtitle">
            {totalItems} user{totalItems !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="page-header__actions">
          <button type="button" className="btn btn-secondary" onClick={() => void refetch()}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      <TableFilters
        compact
        rows={[
          {
            items: [
              {
                type: 'search',
                id: 'regular-user-search',
                label: 'Email',
                placeholder: 'Search by email...',
                value: search,
                onChange: setSearch,
              },
              {
                type: 'search',
                id: 'provider-filter',
                label: 'Provider',
                placeholder: 'Filter by provider...',
                value: providerFilter,
                onChange: setProviderFilter,
                narrow: true,
              },
            ],
          },
        ]}
        onClear={clearFilters}
        showClear={hasActiveFilters}
      />

      {isLoading && (
        <div className="empty-state">
          <p>Loading users...</p>
        </div>
      )}

      {error && (
        <div className="page-error" role="alert">
          <span>{error instanceof Error ? error.message : 'Failed to load users'}</span>
        </div>
      )}

      {!isLoading && !error && users.length === 0 && (
        <div className="empty-state">
          <Users size={40} strokeWidth={1} />
          <p>No users found.</p>
        </div>
      )}

      {!isLoading && !error && users.length > 0 && (
        <>
          <div className="table-container table-container--scrollable">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Surname</th>
                  <th>Country</th>
                  <th>Type</th>
                  <th>DP ID</th>
                  <th>Provider</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user: UserResponse) => (
                  <tr
                    key={user.id}
                    className="data-table__row--clickable"
                    onClick={() => setSelectedUser(user)}
                  >
                    <td>
                      <span className="text-sm" title={user.email}>
                        {user.email}
                      </span>
                    </td>
                    <td>
                      <span className="text-sm">{user.name || '—'}</span>
                    </td>
                    <td>
                      <span className="text-sm">{user.surname || '—'}</span>
                    </td>
                    <td>
                      <span className="text-sm">{user.countryCode?.toUpperCase() ?? '—'}</span>
                    </td>
                    <td>
                      <span className="text-sm">{getProfessionalType(user)}</span>
                    </td>
                    <td>
                      <span className="text-sm">{formatDpId(user)}</span>
                    </td>
                    <td>
                      <span className="text-sm">{user.provider ?? '—'}</span>
                    </td>
                    <td>
                      <span className="text-sm">{formatDate(user.createdAt)}</span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedUser(user);
                        }}
                      >
                        View Votes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <span className="pagination__info">
                {startIndex}&ndash;{endIndex} of {totalItems}
              </span>

              <div className="pagination__controls">
                <button
                  type="button"
                  className="pagination__btn"
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  onClick={() => goToPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>

                {getPageRange(currentPage, totalPages).map((page) =>
                  page < 0 ? (
                    <span key={page} className="pagination__ellipsis">
                      &hellip;
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      className={`pagination__btn${page === currentPage ? ' pagination__btn--active' : ''}`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  type="button"
                  className="pagination__btn"
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                  onClick={() => goToPage((p) => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <UserVotesModal
        isOpen={selectedUser !== null}
        onClose={() => setSelectedUser(null)}
        user={selectedUser}
      />
    </>
  );
}
